import React, { createContext, useContext, useReducer, useEffect, useState, useRef, type ReactNode } from 'react';
import type { Product, CartItem } from '../types/Product';
import { productService, abandonedCartService } from '../services';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

interface CartState {
  items: CartItem[];
  total: number;
  itemCount: number;
}

type CartAction =
  | { type: 'ADD_TO_CART'; payload: Product }
  | { type: 'REMOVE_FROM_CART'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'REMOVE_EXPIRED_ITEMS'; payload: string[] }
  | { type: 'LOAD_CART'; payload: CartItem[] };

interface CartContextType extends CartState {
  addToCart: (product: Product) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getItemQuantity: (productId: string) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

// Constants
const CART_EXPIRATION_HOURS = 24;
const CART_STORAGE_KEY = 'firecrackers_cart';
const EXPIRATION_CHECK_INTERVAL = 60000; // Check every minute
const SYNC_DEBOUNCE_DELAY = 1000; // 1 second

// Helper function to check if an item is expired
const isItemExpired = (addedAt: number): boolean => {
  const now = Date.now();
  const expirationTime = addedAt + (CART_EXPIRATION_HOURS * 60 * 60 * 1000);
  return now > expirationTime;
};

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItem = state.items.find(item => item.id === action.payload.id);
      
      if (existingItem) {
        const updatedItems = state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
        
        return {
          ...state,
          items: updatedItems,
          total: updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
          itemCount: updatedItems.reduce((sum, item) => sum + item.quantity, 0)
        };
      }
      
      const newItems = [...state.items, { ...action.payload, quantity: 1, addedAt: Date.now() }];
      return {
        ...state,
        items: newItems,
        total: newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        itemCount: newItems.reduce((sum, item) => sum + item.quantity, 0)
      };
    }
    
    case 'REMOVE_FROM_CART': {
      const newItems = state.items.filter(item => item.id !== action.payload);
      return {
        ...state,
        items: newItems,
        total: newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        itemCount: newItems.reduce((sum, item) => sum + item.quantity, 0)
      };
    }
    
    case 'UPDATE_QUANTITY': {
      if (action.payload.quantity <= 0) {
        return cartReducer(state, { type: 'REMOVE_FROM_CART', payload: action.payload.id });
      }
      
      const updatedItems = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: action.payload.quantity }
          : item
      );
      
      return {
        ...state,
        items: updatedItems,
        total: updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        itemCount: updatedItems.reduce((sum, item) => sum + item.quantity, 0)
      };
    }
    
    case 'CLEAR_CART':
      return {
        items: [],
        total: 0,
        itemCount: 0
      };
    
    case 'REMOVE_EXPIRED_ITEMS': {
      const newItems = state.items.filter(item => !action.payload.includes(item.id));
      return {
        ...state,
        items: newItems,
        total: newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        itemCount: newItems.reduce((sum, item) => sum + item.quantity, 0)
      };
    }
    
    case 'LOAD_CART': {
      return {
        ...state,
        items: action.payload,
        total: action.payload.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        itemCount: action.payload.reduce((sum, item) => sum + item.quantity, 0)
      };
    }
    
    default:
      return state;
  }
};

const initialState: CartState = {
  items: [],
  total: 0,
  itemCount: 0
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [isInitialized, setIsInitialized] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncRef = useRef<string>('');

  // Load cart from localStorage and backend on mount
  useEffect(() => {
    const loadCart = async () => {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      let localItems: CartItem[] = [];
      
      if (savedCart) {
        try {
          const parsedCart: CartItem[] = JSON.parse(savedCart);
          // Add addedAt to items that don't have it (backward compatibility)
          localItems = parsedCart.map(item => ({
            ...item,
            addedAt: item.addedAt || Date.now()
          }));
          // Filter out expired items
          localItems = localItems.filter(item => !isItemExpired(item.addedAt));
        } catch (error) {
          console.error('Error loading cart from localStorage:', error);
        }
      }
      
      // If authenticated, try to load from backend and merge
      if (isAuthenticated && user) {
        try {
          const response = await abandonedCartService.getMyCart();
          if (response.success && response.data) {
            // Fetch product details for backend cart items
            const backendItems: CartItem[] = [];
            
            for (const item of response.data.items) {
              // Backend items have productId, need to fetch full product details
              const productId = (item as any).productId || item.id;
              const productResponse = await productService.getProductById(productId);
              if (productResponse.success && productResponse.data) {
                const product = productResponse.data;
                backendItems.push({
                  ...product,
                  quantity: item.quantity,
                  addedAt: new Date((item as any).addedAt || Date.now()).getTime()
                });
              }
            }
            
            // Merge local and backend items (local takes precedence for quantity)
            const mergedItems = [...localItems];
            backendItems.forEach(backendItem => {
              const existingIndex = mergedItems.findIndex(item => item.id === backendItem.id);
              if (existingIndex === -1) {
                mergedItems.push(backendItem);
              }
            });
            
            if (mergedItems.length > 0) {
              dispatch({ type: 'LOAD_CART', payload: mergedItems });
            }
          }
        } catch (error) {
          console.error('Error loading cart from backend:', error);
          // Fall back to local cart
          if (localItems.length > 0) {
            dispatch({ type: 'LOAD_CART', payload: localItems });
          }
        }
      } else {
        // Not authenticated, just use local cart
        if (localItems.length > 0) {
          dispatch({ type: 'LOAD_CART', payload: localItems });
        }
      }
      
      setIsInitialized(true);
    };
    
    loadCart();
  }, [isAuthenticated, user]);

  // Save cart to localStorage and sync with backend
  useEffect(() => {
    if (isInitialized) {
      // Save to localStorage
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state.items));
      
      // Sync with backend if authenticated
      if (isAuthenticated && user) {
        // Cancel previous sync timeout
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current);
        }
        
        // Create a string representation of current cart for comparison
        const currentCartString = JSON.stringify(state.items.map(item => ({ id: item.id, quantity: item.quantity })));
        
        // Only sync if cart has changed
        if (currentCartString !== lastSyncRef.current) {
          syncTimeoutRef.current = setTimeout(async () => {
            try {
              await abandonedCartService.updateCart(state.items);
              lastSyncRef.current = currentCartString;
            } catch (error) {
              console.error('Error syncing cart with backend:', error);
            }
          }, SYNC_DEBOUNCE_DELAY);
        }
      }
    }
  }, [state.items, isInitialized, isAuthenticated, user]);

  // Check for expired items periodically
  useEffect(() => {
    if (!isInitialized) return;

    const checkExpiredItems = () => {
      const expiredItemIds = state.items
        .filter(item => item.addedAt && isItemExpired(item.addedAt))
        .map(item => item.id);
      
      if (expiredItemIds.length > 0) {
        dispatch({ type: 'REMOVE_EXPIRED_ITEMS', payload: expiredItemIds });
        showToast(`${expiredItemIds.length} expired items removed from cart`, 'info');
      }
    };

    checkExpiredItems();
    const interval = setInterval(checkExpiredItems, EXPIRATION_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [state.items, isInitialized, showToast]);

  const addToCart = (product: Product) => {
    dispatch({ type: 'ADD_TO_CART', payload: product });
  };

  const removeFromCart = (productId: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  };

  const clearCart = async () => {
    dispatch({ type: 'CLEAR_CART' });
    localStorage.removeItem(CART_STORAGE_KEY);
    
    // Clear backend cart if authenticated
    if (isAuthenticated && user) {
      try {
        await abandonedCartService.clearCart();
      } catch (error) {
        console.error('Error clearing backend cart:', error);
      }
    }
  };

  const getItemQuantity = (productId: string): number => {
    const item = state.items.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  return (
    <CartContext.Provider
      value={{
        ...state,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getItemQuantity
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};